import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "react-bootstrap";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { LoadingContainer } from "go-core/components/Loading";
import {
	FILTER_SEPARATOR,
	FILTER_VALUE_SEPARATOR,
	NEW_WAY_TO_ENCODING_FILTER_SIGN,
} from "go-list/core/components/Filter/services";
import { ReportComboItem } from "./ReportComboItem";
import { replacePredefinedRanges } from "./User/services/comboDateUtils";
import { ReportComboContext } from "./User/services/context";
import { comboFlats } from "./User/services/services";
import {
	ConfigReportComboApi,
	DataColumnConfigReportComboApi,
	DataConfigReportComboApi,
	FlatReportComboApi,
	ReportResponseReportComboApi,
	ResponseReportComboApi,
	ValidationReportComboException,
} from "./User/services/types";
import { ReportComboUtils } from "./User/services/utils";
import { VariableReportComboUtils } from "./User/services/variableUtils";

interface Props {
	config: ConfigReportComboApi;
	date?: Date;
	variableValues: Record<string, string>;
}

const parseConfig = (
	kpiConfig: ConfigReportComboApi,
	date: Date,
	variableValues: Record<string, string>
): ConfigReportComboApi => {
	const newKpiConfig = JSON.parse(JSON.stringify(kpiConfig)) as ConfigReportComboApi;
	for (const kpiConfigData of newKpiConfig.data) {
		for (const kpiConfigDataColumn of kpiConfigData.columns) {
			kpiConfigDataColumn.filter = replacePredefinedRanges(
				kpiConfigDataColumn.filter,
				date,
				kpiConfig.variables,
				variableValues
			);
		}
	}
	if (newKpiConfig.columns) {
		for (const kpiConfigColumn of newKpiConfig.columns) {
			if (kpiConfigColumn.values_dynamic) {
				VariableReportComboUtils.getDynamicValues(
					kpiConfigColumn.values_dynamic,
					variableValues,
					kpiConfig.variables
				);
				const newValues = VariableReportComboUtils.getDynamicValues(
					kpiConfigColumn.values_dynamic,
					variableValues,
					kpiConfig.variables
				);
				if (newValues) {
					kpiConfigColumn.values = newValues;
				}
			}
		}
	}
	newKpiConfig.table?.forEach((table) => {
		table.columns.forEach((kpiConfigColumn) => {
			if (kpiConfigColumn.values_dynamic) {
				const newValues = VariableReportComboUtils.getDynamicValues(
					kpiConfigColumn.values_dynamic,
					variableValues,
					kpiConfig.variables
				);
				if (newValues) {
					kpiConfigColumn.values = newValues;
				}
			}
		});
	});
	return newKpiConfig;
};

export const ReportCombo = (props: Props) => {
	const [data, setData] = useState<ResponseReportComboApi>();
	const [loading, setLoading] = useState(true);
	const date = props.date ? props.date : new Date();
	const kpiConfig = parseConfig(props.config, date, props.variableValues);
	const reportComboService = React.useContext(ReportComboContext);
	const cfgJson = JSON.stringify(props.config);
	const { addFlash } = useFlash();

	useEffect(() => {
		fetch();
	}, [cfgJson]);

	const fetchData = async (
		dataConfig: DataConfigReportComboApi,
		dataColumnId: string,
		newFilter: string,
		groups: string[]
	) => {
		const params = {
			f: btoa(unescape(encodeURIComponent(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}${FILTER_SEPARATOR}${newFilter}`))),
			groups: groups.join(FILTER_VALUE_SEPARATOR),
		};
		if (dataConfig.resource_url) {
			const newParams = JSON.parse(JSON.stringify(params)) as any;
			return fetchDataResourceUrl(dataConfig.resource_url, dataConfig.id, dataColumnId, newParams);
		}
		return await reportComboService.api().get(dataConfig.resource, dataConfig.id, dataColumnId, params);
	};
	const fetchDataResourceUrl = async (
		resourceUrl: string,
		dataId: string,
		dataColumnId: string,
		params: Record<string, string>,
		options?: Record<string, any>
	) => {
		let res = await axios.get(resourceUrl, { params, cancelToken: options?.cancelToken });
		return {
			id: dataId,
			column_id: dataColumnId,
			data: res.data.data.reports[0],
		} as ReportResponseReportComboApi;
	};
	const fetchCompare = async (
		dataColumnConfig: DataColumnConfigReportComboApi,
		dataConfig: DataConfigReportComboApi
	) => {
		if (!dataColumnConfig.compare) return;

		const dataColumnCompareConfig = dataColumnConfig.compare;
		let newFilter = dataConfig.filter;
		let groups = ["NONE"];
		// dataColumnCompareConfig.filter = replacePredefinedRanges(dataColumnCompareConfig.filter, date, props.config.variables);

		if (dataColumnCompareConfig.filter) {
			newFilter += `${FILTER_SEPARATOR}${dataColumnCompareConfig.filter}`;
		}

		if (dataColumnConfig?.group && dataColumnConfig.group !== "NONE") {
			groups.push(dataColumnConfig.group);
		}
		groups.push(...dataConfig.groups.filter((x) => x !== "NONE"));

		if (groups.length === 0) groups = ["NONE"];

		try {
			const res = await fetchData(dataConfig, dataColumnConfig.id, newFilter, groups);
			return res.data ? res.data : undefined;
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetch = async () => {
		setLoading(true);
		const reports: ReportResponseReportComboApi[] = [];
		for (const dataConfig of kpiConfig.data) {
			const filter = dataConfig.filter;
			for (const dataColumnConfig of dataConfig.columns) {
				let newFilter = filter;
				// dataColumnConfig.filter = replacePredefinedRanges(dataColumnConfig.filter, date, props.config.variables);  //Probably  we can remove it
				if (dataColumnConfig.filter) {
					newFilter += `${FILTER_SEPARATOR}${dataColumnConfig.filter}`;
				}
				let groups = ["NONE"];
				if (dataColumnConfig?.group && dataColumnConfig.group !== "NONE") {
					groups.push(dataColumnConfig.group);
				}
				groups.push(...dataConfig.groups.filter((x) => x !== "NONE"));
				if (groups.length === 0) groups = ["NONE"];

				try {
					const res = await fetchData(dataConfig, dataColumnConfig.id, newFilter, groups);
					if (res) {
						if (dataColumnConfig.compare) {
							const compareResponse = await fetchCompare(dataColumnConfig, dataConfig);
							if (compareResponse) {
								res.compare_data = compareResponse;
							}
						}
						reports.push(res);
					}
				} catch (err) {
					handleError.alert(err, addFlash);
				}
			}
		}

		setData({
			id: kpiConfig.id,
			name: kpiConfig.name,
			reports,
		});
		setLoading(false);
	};

	const renderLoading = () => {
		return (
			<Card className={"churn-card mb-4"} style={{ minHeight: 300 }}>
				<Card.Body>
					<LoadingContainer />
				</Card.Body>
			</Card>
		);
	};

	if (!kpiConfig) return <>ERROR</>;
	if (!data) return renderLoading();
	if (loading) return renderLoading();

	let results: FlatReportComboApi[] | undefined = undefined;

	try {
		results = comboFlats(data, kpiConfig);
		// results = comboFlat(data, kpiConfig);
	} catch (err: any) {
		if (err instanceof ValidationReportComboException) {
			return <>ERROR: {err.message || err.toString()}</>;
		}
		throw err;
	}

	return (
		<>
			{results?.map((result) => {
				return (
					<React.Fragment key={result.id}>
						<ReportComboItem config_flat={result} config={kpiConfig} />
						{/*{renderComboReport(result)}*/}
					</React.Fragment>
				);
			})}
		</>
	);
};
